import { ApolloClient } from 'apollo-client';
import { HttpLink, createHttpLink } from 'apollo-link-http';
import DebounceLink from 'apollo-link-debounce';
import { setContext } from 'apollo-link-context';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { auth } from './auth/AuthService';
import { store } from './index';
import uuid from 'uuid/v4';

declare var USE_AUTH;

// Apollo configuration, https://github.com/Akryum/vue-apollo

let renewPromise = null;

// Custom fetch to handle expired auth tokens
// https://blog.beeaweso.me/refreshing-token-based-authentication-with-apollo-client-2-0-7d45c20dc703
// https://github.com/newsiberian/apollo-link-token-refresh
// https://auth0.com/docs/quickstart/spa/vanillajs/05-token-renewal
const customFetch = async (uri, options) => {

  // Create initial fetch, this is what would normally be executed in the link without the override
  const response = await fetch(uri, options);

  // Check if request failed due to unauthorized access
  if (response.status === 401) {

    try {

      // Renew token, check if another request is already renewing auth
      if (renewPromise) {
        await renewPromise;
      } else {
        renewPromise = auth.renew();
        await renewPromise;
        renewPromise = null;
      }

      // console.log(`fetch with token ${store.state.auth.accessToken}`);

      // Fetch again with new credentials
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${store.state.auth.accessToken}`,
      };

      return fetch(uri, options);
    } catch (e) {
      console.log(`Auth renew failed, redirecting to login`, e);
      auth.login();
    }
  }

  if (response.status === 502) {
    console.log(`Got 502 Bad Gateway, retrying the fetch`);
    // Just retry the fetch if we get bad gateway error
    return customFetch(uri, options);
  }

  // Check if response is ok
  if (response.status !== 200) {
    const r = response.clone();
    const body = await r.text();
    console.log(`Got unexpected response`, response, body);
    store.commit('setError', {
      msg: body,
      permissionError: false,
    });
  }

  return response;
};

const httpLink = new HttpLink({
  // According to the Apollo docs, this should be an absolute URI.
  uri: `${location.origin}/graphql`,
  // uri: `http://localhost:3355/graphql`,
  // credentials: 'same-origin',
  // credentials: 'include',
  fetch: customFetch,
});

const debounceLink = new DebounceLink(100);


// https://www.apollographql.com/docs/react/recipes/authentication.html#Header
// https://www.apollographql.com/docs/link/links/context.html#Usage
const authLink = setContext((_, { headers }) => {
  return new Promise(async (resolve, reject) => {
    await auth.initPromise;

    if (!store.getters.authenticated) {
      await auth.renew();
    }

    const token = store.state.auth.accessToken;

    // console.log(`authLink adding token `, token);

    resolve({
      headers: {
        ...headers,
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Developer-Mode': store.state.developerMode,
        'X-Request-ID': uuid(),
      },
    });
  });
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  let permissionError = false;
  if (graphQLErrors) {
    const errorMsgList = graphQLErrors.map(({ message, locations, path }) => {
      if (path.length === 1 && (path[0] === 'reportProblem' || path[0] === 'registerUser')) {
        // This is handled by the component.
        return null;
      }

      if (message === 'Insufficient Permissions.' ||
        message.startsWith('User is not member of project') ||
        message.endsWith('could not be found'))
      {
        permissionError = true;
      }

      console.log(`[GraphQL error]: Message: ${message}, Path: ${path}, Location:`, location);
      return `[GraphQL error]: Message: ${message}`;
    }).filter( msg => msg !== null );

    if (permissionError) {
      store.commit('setError', {
        // tslint:disable-next-line
        msg: 'Insufficient Permissions. Please logout and log in again if your permissions have been changed by the administrator.',
        permissionError: true,
      });

    } else if (errorMsgList.length > 0) {
      store.commit('setError', {
        msg: errorMsgList,
        permissionError: false,
      });
    }

  }

  if (networkError && !permissionError) {
    console.log(`[Network error]: ${networkError}`);
    store.commit('setError', {
      msg: networkError,
      permissionError: false,
    });
  }
});

let link = debounceLink.concat(errorLink).concat(httpLink);

if (USE_AUTH) {
  link = authLink.concat(link);
}

// Create the apollo client
const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  connectToDevTools: true,
});

export { apolloClient };



// WEBPACK FOOTER //
// ./src/apollo.ts